import type { ChapterContent, FirstGameStep } from '../../types';

/**
 * Projekt Gaja — Content Rewrite 3.0 / docs/cursor CONTENT LANE
 * Source: docs/perfect-quality/02_NOWE_GRY_PELNE_INSTRUKCJE/09_PROJEKT_GAJA_ZAGINIONA_FLOTA_PELNA_INSTRUKCJA.md
 * ONE EXPANSION = ONE CHAPTER: Zaginiona Flota (`lost-fleet`) with internal headings only.
 * Faction boards, tech tiles, boosters, goals, ships, exploration boards and artefacts remain PARTIAL blockers.
 * Stable chapter titles + First Game step ids (1–7); TTS fragments use block indices within each chapter.
 */
export const projektGajaChapters: ChapterContent[] = [
  {
    title: '1. Cel gry i granice tej instrukcji',
    content: [
      {
        type: 'warning',
        text: 'Ten podręcznik uczy przepływu Projektu Gaja z lokalnego szkicu Perfect Quality. Indywidualne zdolności frakcji, kafle technologii, boostery, cele, statki, plansze eksploracji i artefakty dodatku nie są tu katalogowane w pełnym tekście — odczytujesz je z komponentów. Nie dopowiadaj wartości z pamięci. [DO WERYFIKACJI: katalogi frakcji, technologii, boosterów, celów, statków, artefaktów oraz dokładna reguła remisu właściwego wydania].',
      },
      {
        type: 'heading',
        text: 'Co robisz i po co',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przez 6 rund rozwijasz międzygwiezdną cywilizację. Kolonizujesz planety, ulepszasz budynki, rozwijasz technologie, tworzysz federacje i wykonujesz Projekty Gaja. Zdobywasz punkty w trakcie gry, za cele rund oraz na końcu za cele końcowe, pozycje na torach badań i pozostałe zasoby.',
      },
      {
        type: 'heading',
        text: 'Kiedy wygrywasz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wygrywa gracz z największą liczbą punktów po rozliczeniu końca gry. Remisy rozstrzyga się według oficjalnej kolejności zapisanej w sekcji punktowania i katalogu wariantu właściwego wydania — tej kolejności nie zgaduj z pamięci.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna buduje kopalnie i stacje, żeby domknąć federację i zebrać token. Bartek goni tory badań i cele rund. Oboje patrzą na ten sam tor punktowy: nie „kto zrobił więcej akcji”, tylko kto zbierze więcej punktów z bieżących celów, federacji, badań i końca gry.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktować partię jak wyścig o mapę bez liczenia punktów z celów rund i końca gry. Mapę budujesz po to, by zdobywać punkty.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: przebieg rundy — rozdział 4; punktowanie końcowe — rozdział 21. Dodatek Zaginiona Flota — jeden rozdział ekspansji (20).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel: najwięcej punktów po 6 rundach. P: Czy ta instrukcja zastępuje teksty frakcji i technologii? O: Nie — katalogi są do weryfikacji; komponenty mają pierwszeństwo. P: Czy remis jest „kto pierwszy”? O: Nie — użyj oficjalnej kolejności wydania.',
      },
    ],
  },
  {
    title: '2. Najważniejsze elementy',
    content: [
      {
        type: 'heading',
        text: 'Co leży na stole i do czego służy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zanim wykonasz pierwszą akcję, rozpoznaj elementy, które wracają w każdej rundzie. Bez tej mapy łatwo pomylić dochód z pulą mocy albo federację z samym zasięgiem.',
      },
      {
        type: 'step-list',
        steps: [
          'modułowa mapa kosmosu;',
          'planety w różnych kolorach;',
          'plansze frakcji;',
          'kopalnie, stacje handlowe, laboratoria, akademie i instytut planetarny;',
          'zasoby: kredyty, ruda, wiedza i Q.I.C.;',
          'znaczniki mocy w trzech misach;',
          'tory badań;',
          'technologie standardowe i zaawansowane;',
          'kafle federacji;',
          'boostery rund;',
          'cele rund i cele końcowe;',
          'Gaiaformery i planety Transdim;',
          'w Zaginionej Flocie: sektory głębokiej przestrzeni, statki, plansze eksploracji, artefakty, nowe technologie, boostery, punktowanie i cztery frakcje.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład rozpoznania',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na planszy frakcji widzisz tor dochodu i misy mocy. Na mapie — planety i pustkę między nimi. Na stole wspólnym — boostery, cele rundy, technologie i tokeny federacji. Gdy ktoś mówi „ładuję moc”, sięgasz po znaczniki w misach, a nie po nowe żetony z pudełka.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: mylić boostery rund z tokenami federacji albo traktować Gaiaformer jak zwykłą kopalnię.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: zasoby — rozdział 5; moc — rozdział 6; dodatek — rozdział 20.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Stół dzieli się na mapę, planszę frakcji i wspólne kafle. P: Czy Zaginiona Flota jest wymagana do nauki podstawy? O: Nie — First Game zaleca partię bez dodatku. P: Czy wszystkie budynki leżą od razu na mapie? O: Nie — zaczynasz od setupu frakcji i dokładasz je akcjami.',
      },
    ],
  },
  {
    title: '3. Przygotowanie',
    content: [
      {
        type: 'heading',
        text: 'Po co setup jest w tej kolejności',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przygotowanie ustawia mapę, cele, boostery i frakcje tak, by pierwsza faza dochodu miała sens. Pominięcie kroku zwykle wychodzi dopiero przy pierwszej federacji albo pierwszym boosterze.',
      },
      {
        type: 'heading',
        text: 'Mapa i układy sektorów',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Mapa jest modułowa: składasz sektory w układzie wskazanym dla liczby graczy. Od tego układu zależy zasięg między planetami, sąsiedztwo przy pasywnym ładowaniu mocy i to, które planety w ogóle da się legalnie skolonizować w pierwszej rundzie. W podstawowej grze używasz wskazanego układu sektorów podstawy; nie przenoś układu z innej liczby graczy „bo wygląda podobnie”.',
      },
      {
        type: 'paragraph',
        text: 'Dokładne diagramy układów 2/3/4 graczy względem Twojego wydania pozostają do weryfikacji na komponencie / w oficjalnym setupie. [DO WERYFIKACJI: dokładne układy map 2/3/4 graczy względem oficjalnego wydania].',
      },
      {
        type: 'heading',
        text: 'Procedura przygotowania podstawy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Zbuduj mapę odpowiednią dla liczby graczy. W podstawowej grze użyj wskazanego układu sektorów.',
          'Rozłóż planszę badań, technologie i płytki zaawansowane.',
          'Wylosuj cele rund i cele końcowe.',
          'Rozłóż boostery w liczbie wymaganej dla graczy.',
          'Gracze wybierają frakcje i przygotowują plansze.',
          'Umieść początkowe budynki zgodnie z kolejnością setupu frakcji.',
          'Wybierz boostery startowe w odwrotnej kolejności.',
          'Przygotuj zasoby i znaczniki mocy według plansz frakcji.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Przy Zaginionej Flocie zastosuj nowy sposób przygotowania mapy, plansze statków i eksploracji oraz elementy zastępujące podstawowe akcje Q.I.C. Dokładna liczba elementów zależy od liczby graczy i wybranego układu — szczegóły w jednym rozdziale dodatku „Zaginiona Flota”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przy 3 graczach Anna układa wskazany układ sektorów dla trzech osób, Bartek rozkłada cele rund i końcowe, Celina liczy boostery według reguły liczby graczy. Dopiero potem wybieracie frakcje, stawiacie startowe budynki zgodnie z kolejnością setupu frakcji i w odwrotnej kolejności bierzecie boostery startowe. Nikt nie bierze boostera „na oko” przed budynkami.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wybrać boostery startowe przed ustawieniem budynków albo użyć układu mapy z innej liczby graczy.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: zasięg na mapie — rozdział 9; dodatek zmienia mapę — rozdział „Zaginiona Flota”. Pierwsza partia — First Game.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Kolejność przygotowania Projektu Gaja',
        fallbackText: 'Schemat: mapa → badania i technologie → cele → boostery → frakcje i budynki startowe → boostery startowe → zasoby i moc.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Setup idzie od mapy do zasobów; boostery startowe bierzesz na końcu, w odwrotnej kolejności. P: Czy mogę grać Zaginioną Flotę w First Game? O: Lokalny przewodnik zaleca najpierw podstawę. P: Skąd brać liczbę boosterów i układ mapy? O: Z reguły liczby graczy i oficjalnego setupu — nie zgaduj.',
      },
    ],
  },
  {
    title: '4. Struktura rundy',
    content: [
      {
        type: 'heading',
        text: 'Jak wygląda zegar 6 rund',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każda z 6 rund ma ten sam szkielet: dochód, Faza Gai, naprzemienne akcje, potem porządkowanie. Jeśli przestawisz kolejność faz, Gaiaformery i dochód mocy przestają być przewidywalne.',
      },
      {
        type: 'heading',
        text: 'A. Faza dochodu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dochód otwiera każdą rundę, zanim cokolwiek zrobicie na mapie. Wszyscy jednocześnie otrzymują zasoby wskazane przez odsłonięte ikony dochodu: na planszy frakcji, na technologiach, na boosterze oraz z innych aktywnych elementów, które w tej chwili dają dochód. To nie jest wybór „co chcę wziąć” — bierzesz to, co odsłonięte ikony wskazują, o ile masz miejsce w limitach torów.',
      },
      {
        type: 'paragraph',
        text: 'Dochód mocy oznacza ładowanie istniejących znaczników przez trzy misy, a nie pobieranie nowych znaczników z pudełka. Jeśli pomylisz te dwie rzeczy, cała gospodarka mocy w partii będzie fałszywa.',
      },
      {
        type: 'heading',
        text: 'B. Faza Gai',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'zakończ aktywne Projekty Gaja;',
          'przekształć odpowiednie planety Transdim w planety Gaja;',
          'przenieś znaczniki mocy z obszaru Gaja zgodnie z zasadami frakcji i toru;',
          'odzyskaj Gaiaformery, gdy zasada na to pozwala.',
        ],
      },
      {
        type: 'heading',
        text: 'C. Faza akcji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze wykonują po jednej akcji zgodnie z ruchem wskazówek zegara. Gracz, który spasował, nie wykonuje już akcji w tej rundzie.',
      },
      {
        type: 'heading',
        text: 'D. Koniec rundy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'odwróć użyte akcje specjalne;',
          'usuń znaczniki z celów rundy, jeśli są jednorazowe;',
          'odzyskaj elementy wskazane przez reguły;',
          'przygotuj następny kafel celu rundy;',
          'kolejność nowej rundy wynika z kolejności pasowania.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przebiegu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Runda 2: wszyscy biorą dochód (Anna ładuje 2 mocy przez misy). W Fazie Gai planeta Anny z Gaiaformerem staje się planetą Gaja. Potem Anna buduje kopalnię, Bartek ulepsza stację, Celina pasuje i ustala miejsce w kolejności. Na końcu odwracacie akcje specjalne i odsłaniacie kolejny cel rundy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: brać dochód mocy jako nowe znaczniki z pudełka albo wykonywać akcję po spasowaniu.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: lista akcji — rozdział 7; pasowanie — rozdział 18; Faza Gai szczegółowo przy Projekcie Gaja — rozdział 13.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Struktura rundy Projektu Gaja',
        fallbackText: 'Schemat rundy: Dochód → Faza Gai → akcje naprzemienne do pasów → porządkowanie i nowa kolejność.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zegar rundy jest sztywny: dochód, Gaja, akcje, porządek. P: Czy po pasie mogę wrócić w tej rundzie? O: Nie. P: Skąd bierze się kolejność następnej rundy? O: Z kolejności pasowania.',
      },
    ],
  },
  {
    title: '5. Zasoby',
    content: [
      {
        type: 'heading',
        text: 'Co trzymasz i kiedy wydajesz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cztery zasoby napędzają różne decyzje. Kredyty i ruda budują gospodarkę mapy, wiedza pcha badania, Q.I.C. jest rzadkim „kluczem” do zasięgu i wybranych akcji.',
      },
      {
        type: 'heading',
        text: 'Kredyty',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najczęściej opłacają budynki, ulepszenia i inne koszty gospodarcze.',
      },
      {
        type: 'heading',
        text: 'Ruda',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Służy głównie do budowy i terraforming.',
      },
      {
        type: 'heading',
        text: 'Wiedza',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pozwala awansować na torach badań.',
      },
      {
        type: 'heading',
        text: 'Q.I.C.',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Rzadki zasób wykorzystywany m.in. do zwiększania zasięgu, niektórych akcji specjalnych i efektów technologii.',
      },
      {
        type: 'paragraph',
        text: 'Zasobów nie można wydawać ponad stan. Limity na torach planszy frakcji są twarde; nadwyżka zazwyczaj przepada, chyba że efekt mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna chce zbudować kopalnię dwa kroki od koloru rodzimego. Najpierw liczy rudę na terraforming i koszt kopalni. Brakuje jej jednego Q.I.C. do zasięgu — nie „pożycza” z przyszłego dochodu; albo zwiększa zasięg innym legalnym sposobem, albo wybiera bliższą planetę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wydawać ponad stan albo trzymać nadwyżkę powyżej twardego limitu toru, jakby miała wrócić w następnej rundzie.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: terraforming — rozdział 10; badania — rozdział 14; moc nie jest czwartym „kolorem kredytu” — rozdział 6.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kredyty/ruda budują, wiedza bada, Q.I.C. odblokowuje rzadkie efekty. P: Czy mogę wydać zasób, którego nie mam, „na kredyt”? O: Nie. P: Czy nadwyżka ponad limit zostaje? O: Zazwyczaj przepada, chyba że efekt mówi inaczej.',
      },
    ],
  },
  {
    title: '6. Moc i trzy misy',
    content: [
      {
        type: 'heading',
        text: 'Po co istnieją trzy misy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Moc nie leży w jednej kupce. Znaczniki krążą przez trzy misy, a wydawać standardowo możesz tylko te w misie III. Ładowanie przesuwa istniejące znaczniki — nie dobiera nowych z pudełka.',
      },
      {
        type: 'heading',
        text: 'Jak ładujesz moc',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'z misy I do II;',
          'z II do III;',
          'tylko znaczniki w misie III można standardowo wydawać.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Gdy otrzymujesz określoną ilość mocy, przesuwasz znaczniki krok po kroku. Najpierw wszystkie dostępne z misy I do II, potem z II do III.',
      },
      {
        type: 'heading',
        text: 'Wydawanie mocy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przenieś wymaganą liczbę znaczników z misy III do misy I.',
      },
      {
        type: 'heading',
        text: 'Palenie mocy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Możesz na stałe usunąć znacznik z misy II, aby przesunąć drugi znacznik z misy II do III. To przyspiesza dostęp do mocy, ale zmniejsza całkowitą pulę. Nie wolno palić znacznika, jeśli nie ma drugiego znacznika do przesunięcia.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bartek ma 4 znaczniki w I, 2 w II, 1 w III i dostaje 3 mocy. Najpierw przesuwa z I do II, potem z II do III, aż „zużyje” trzy kroki ładowania. Jeśli chce szybko mieć 2 w III, może spalić jeden znacznik z II, by przesunąć drugi do III — ale traci ten spalony na stałe.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktować ładowanie mocy jak dobieranie nowych znaczników albo palić moc, gdy w misie II nie ma pary do przesunięcia.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: palenie mocy to czynność darmowa w dozwolonym momencie, nie akcja główna (FAQ). Obszar Gaja przy Projekcie Gaja — rozdział 13.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Moc krąży I→II→III; wydajesz z III. P: Czy palenie mocy jest akcją główną? O: Nie. P: Czy mogę spalić jedyny znacznik w II? O: Nie — potrzebujesz drugiego do przesunięcia.',
      },
    ],
  },
  {
    title: '7. Akcje główne',
    content: [
      {
        type: 'heading',
        text: 'Co wolno w swojej turze',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W fazie akcji, gdy przychodzi Twoja kolej i jeszcze nie spasowałeś, wykonujesz dokładnie jedną akcję główną. To jest rdzeń zegara rundy: jedna decyzja na turę, potem następny gracz zgodnie z ruchem wskazówek zegara.',
      },
      {
        type: 'step-list',
        steps: [
          'zbuduj kopalnię;',
          'rozpocznij Projekt Gaja;',
          'ulepsz budynek;',
          'utwórz federację;',
          'awansuj na torze badań;',
          'wykonaj akcję mocy lub Q.I.C.;',
          'wykonaj akcję specjalną;',
          'spasuj.',
        ],
      },
      {
        type: 'heading',
        text: 'Akcje darmowe (free actions)',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Efekty oznaczone jako czynność darmowa („free action”) nie zużywają Twojej jednej akcji głównej. Możesz je wykonywać przed, po i pomiędzy krokami akcji głównej, o ile nie przerywają nierozdzielnego efektu. Klasyczny przykład z FAQ: palenie mocy jest czynnością darmową w dozwolonym momencie, a nie akcją główną z listy powyżej.',
      },
      {
        type: 'paragraph',
        text: 'Granica jest praktyczna: jeśli efekt mówi, że musisz go rozwiązać w całości (np. pełna procedura budowy), nie wbijasz w środek darmowej czynności tak, jakby to była osobna tura. Domknij nierozdzielny efekt, a darmowe rzeczy rób w legalnych „szwach” przed/po/pomiędzy.',
      },
      {
        type: 'heading',
        text: 'Akcje specjalne',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Akcja specjalna to jedna z pozycji na liście akcji głównych. Jej źródłem bywa booster, technologia albo inne aktywne pole z tekstem „akcja specjalna”. Po wykonaniu takiej akcji zwykle zakrywasz albo odwracasz użyte pole — na końcu rundy odwracasz użyte akcje specjalne z powrotem (patrz porządkowanie w rozdziale 4). Konkretny efekt odczytujesz z kafla lub planszy; nie zgaduj wartości z pamięci. [DO WERYFIKACJI: katalog boosterów i technologii dających akcje specjalne].',
      },
      {
        type: 'heading',
        text: 'Przykład wyboru akcji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Celina ma zasięg i rudę na kopalnię, ale cel rundy nagradza ulepszenie. Najpierw sprawdza, czy ma legalne ulepszenie; jeśli tak, bierze punkty celu teraz. Przed akcją może spalić moc (free action), żeby mieć moc na satelity później — to nie liczy się jako druga akcja główna. Jeśli nie ma legalnego ulepszenia, buduje kopalnię pod przyszłe ulepszenie i kończy turę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wkładać darmową czynność w środek nierozdzielnego efektu, traktować palenie mocy jak akcję główną albo próbować drugiej akcji głównej w tej samej turze.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: szczegóły poszczególnych akcji — rozdziały 8–18; pasowanie — 18; wspólne pola mocy/Q.I.C. — 17. Jedna tura = jedna akcja główna (+ dozwolone free actions).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jedna tura, jedna akcja główna; free action nie zużywa turu; pas zamyka Twój udział w rundzie. P: Czy free action liczy się jako akcja główna? O: Nie. P: Czy akcja specjalna to free action? O: Nie — to jedna z akcji głównych z listy, zwykle z kafla/technologii.',
      },
    ],
  },
  {
    title: '8. Budowa kopalni',
    content: [
      {
        type: 'heading',
        text: 'Po co budujesz kopalnię',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kopalnia to podstawowy sposób zajęcia planety. Od niej zaczyna się sieć struktur, federacje i wiele celów rund.',
      },
      {
        type: 'heading',
        text: 'Procedura budowy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz niezamieszkaną planetę w zasięgu;',
          'określ liczbę kroków terraforming dzielącą jej kolor od rodzimego koloru frakcji;',
          'opłać terraforming;',
          'opłać koszt kopalni;',
          'umieść najdalej wysuniętą w lewo kopalnię z planszy frakcji;',
          'zdobądź punkty celu rundy, technologii lub efektów, jeśli warunki są spełnione.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Planeta Gaja może być kolonizowana przez wszystkie frakcje według osobnego kosztu. Planeta Transdim nie może otrzymać zwykłej kopalni przed ukończeniem Projektu Gaja.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna wybiera pustą planetę w zasięgu 2. Kolor wymaga 2 kroków terraformingu — płaci rudę za kroki, potem koszt kopalni, bierze skrajną lewą kopalnię z planszy i sprawdza, czy cel rundy właśnie nagradza budowę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: budować na planecie zajętej przez przeciwnika, pomijać koszt terraformingu albo stawiać kopalnię na Transdim przed ukończeniem Projektu Gaja.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: zasięg — rozdział 9; terraforming — rozdział 10; Projekt Gaja — rozdział 13.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zasięg + terraforming + koszt = jedna akcja budowy. P: Czy mogę zbudować na planecie przeciwnika? O: Nie. P: Czy Gaja wymaga terraformingu jak zwykły kolor? O: Ma osobny koszt kolonizacji — sprawdź regułę Gai, nie zgaduj.',
      },
    ],
  },
  {
    title: '9. Zasięg i nawigacja',
    content: [
      {
        type: 'heading',
        text: 'Jak mierzysz dystans',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zasięgiem odpowiadasz na pytanie: „czy ta planeta jest wystarczająco blisko moich struktur, żeby wykonać budowę, Projekt Gaja albo inną akcję wymagającą zasięgu?”. Liczysz go jako najmniejszą liczbę połączeń między polami kosmosu. Nie musi to być „prosta linia” na stole — liczy się legalna ścieżka pól.',
      },
      {
        type: 'heading',
        text: 'Tor Nawigacji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podstawowy zasięg zależy od pozycji na torze Nawigacji (jednym z torów badań) oraz od zdolności frakcji. Awans na torze Nawigacji zwiększa to, jak daleko sięgasz bez wydawania Q.I.C. Zdolności frakcji mogą ten zasięg dalej modyfikować — odczytuj je z planszy frakcji, bez dopowiadania z pamięci.',
      },
      {
        type: 'heading',
        text: 'Jak zwiększasz zasięg',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wydając Q.I.C.;',
          'używając boostera lub technologii;',
          'korzystając ze zdolności frakcji.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Tymczasowe zwiększenie (np. Q.I.C. przy konkretnej akcji) dotyczy tej czynności — nie zostawia „stałego +1 na zawsze”, chyba że efekt wyraźnie tak mówi. Stały wzrost zwykle pochodzi z toru Nawigacji, technologii lub zdolności.',
      },
      {
        type: 'heading',
        text: 'Przykład odległości',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Od Twojej kopalni do celu są dwie legalne ścieżki: przez 2 pola albo przez 3. Liczy się krótsza (2). Jeśli Twój podstawowy zasięg z Nawigacji to 1, potrzebujesz legalnego zwiększenia (Q.I.C., booster, technologia lub zdolność), zanim zbudujesz. Trasa „na skróty” przez niedozwolone połączenie nie istnieje — nawet jeśli wizualnie wygląda bliżej.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: liczyć zasięg po linii prostej zamiast po najmniejszej liczbie połączeń, mylić tymczasowe Q.I.C. ze stałym awansem Nawigacji albo zapominać o zwiększeniu z boostera.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: w przykładach aplikacja powinna pokazywać mapę z odległością 1, 2, 3 i trasą niedozwoloną. Budowa — rozdział 8; tory badań — 14; Q.I.C. — 5 i 17.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Zasięg jako najkrótsza ścieżka',
        fallbackText: 'Schemat: zasięg = najmniejsza liczba połączeń; baza z toru Nawigacji; Q.I.C., booster, technologia lub zdolność mogą go zwiększyć.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zasięg to najkrótsza ścieżka pól; baza często z toru Nawigacji. P: Czy planeta musi leżeć w prostej linii? O: Nie. P: Czy Q.I.C. zawsze zwiększa zasięg na stałe? O: Zwykle tylko przy tej czynności, gdy wydajesz go zgodnie z regułą zwiększania zasięgu.',
      },
    ],
  },
  {
    title: '10. Terraforming',
    content: [
      {
        type: 'heading',
        text: 'Co oznacza koło kolorów',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koło kolorów na planszy frakcji pokazuje odległość pomiędzy rodzajem planety a planetą macierzystą. Każdy krok kosztuje rudę, a rozwój toru Terraformacji obniża koszt jednego kroku.',
      },
      {
        type: 'heading',
        text: 'Timing w akcji budowy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nie wolno częściowo terraformować planety i pozostawić jej na później. Cały koszt i budowa kopalni stanowią jedną akcję.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Planeta jest 3 kroki od koloru rodzimego. Tor Terraformacji obniżył koszt kroku. Anna płaci pełną rudę za 3 kroki i od razu stawia kopalnię. Nie może „zapłacić za 1 krok dziś i dokończyć jutro”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zostawianie częściowo terraformowanej planety albo pomijanie kosztu terraformingu przy budowie.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: budowa kopalni — rozdział 8. Koszty kroków odczytuj z planszy frakcji i toru — nie z pamięci.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Terraforming jest częścią tej samej akcji co budowa. P: Czy mogę terraformować „na zapas”? O: Nie w sensie częściowej zmiany bez budowy. P: Co obniża koszt kroku? O: Rozwój toru Terraformacji.',
      },
    ],
  },
  {
    title: '11. Ulepszanie budynków',
    content: [
      {
        type: 'heading',
        text: 'Ścieżka ulepszeń',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ulepszenie zamienia własny budynek na wyższy poziom, odsłania lub zakrywa pola dochodu i czasem daje technologię. To nie jest „kolejna kopalnia”.',
      },
      {
        type: 'step-list',
        steps: [
          'kopalnia → stacja handlowa;',
          'stacja handlowa → laboratorium albo instytut planetarny;',
          'laboratorium → akademia.',
        ],
      },
      {
        type: 'heading',
        text: 'Procedura ulepszenia',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz własny budynek;',
          'sprawdź właściwy koszt;',
          'zastosuj niższy koszt stacji handlowej, jeśli spełniasz warunek sąsiedztwa;',
          'zwróć stary budynek na planszę frakcji;',
          'umieść nowy budynek;',
          'przy laboratorium lub akademii pobierz technologię, jeśli należy się w tym momencie.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Odsłonięcie i zakrycie pól zmienia przyszły dochód.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bartek ulepsza kopalnię do stacji. Spełnia warunek sąsiedztwa, więc płaci niższy koszt stacji, wraca kopalnią na planszę, stawia stację i sprawdza cel rundy. Nie bierze technologii — ta należy się przy laboratorium lub akademii, gdy reguła tak stanowi.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: dobieranie technologii przy każdym ulepszeniu, także przy stacji handlowej.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: sąsiedztwo i pasywne ładowanie mocy — rozdział 12; technologie — rozdział 15.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ulepszasz własny budynek po ścieżce; technologia nie spada przy każdym poziomie. P: Czy mogę ulepszyć budynek przeciwnika? O: Nie. P: Czy niższy koszt stacji jest zawsze? O: Tylko gdy spełniasz warunek sąsiedztwa.',
      },
    ],
  },
  {
    title: '12. Sąsiedztwo i pasywne ładowanie mocy',
    content: [
      {
        type: 'heading',
        text: 'Kiedy sąsiad dostaje moc',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy gracz buduje lub ulepsza w sąsiedztwie innych graczy, sąsiedzi mogą otrzymać możliwość ładowania mocy. Liczba mocy zależy od wartości nowego budynku i układu mapy.',
      },
      {
        type: 'heading',
        text: 'Przyjmowanie mocy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracz przyjmujący moc zwykle traci punkty w wysokości o 1 mniejszej niż liczba przyjętej mocy. Może przyjąć mniej albo odmówić. [DO WERYFIKACJI: tabela wartości budynków i dokładne wyjątki frakcji].',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna stawia stację obok Barteka. Bartek może przyjąć ofertę ładowania mocy: liczy, ile mocy dostanie i ile punktów straci (o 1 mniej niż przyjęta moc). Może wziąć mniej albo odmówić całkowicie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wymuszać przyjęcie mocy albo liczyć punkty karne 1:1 zamiast „o 1 mniej niż przyjęta moc”.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: wartości budynków odczytuj z oficjalnej tabeli wydania — nie z pamięci. Budowa i ulepszenia — rozdziały 8 i 11.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sąsiad może dostać moc za Twoją budowę/ulepszenie; może odmówić. P: Czy muszę przyjąć moc? O: Nie. P: Czy strata PZ równa przyjętej mocy? O: Zwykle o 1 mniejsza niż przyjęta moc.',
      },
    ],
  },
  {
    title: '13. Projekt Gaja',
    content: [
      {
        type: 'heading',
        text: 'Po co istnieje Projekt Gaja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Planety Transdim nie biorą zwykłej kopalni od razu. Najpierw rezerwujesz je Gaiaformerem, a w Fazie Gai stają się planetami Gaja — dopiero potem budujesz kopalnię osobną akcją.',
      },
      {
        type: 'heading',
        text: 'Procedura uruchomienia',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz planetę w zasięgu;',
          'zapłać koszt uruchomienia zależny od poziomu toru Projektu Gaja;',
          'przenieś odpowiednią liczbę znaczników mocy do obszaru Gaja;',
          'umieść Gaiaformer na planecie.',
        ],
      },
      {
        type: 'paragraph',
        text: 'W Fazie Gai następnej rundy planeta staje się planetą Gaja. Nadal trzeba wykonać później akcję budowy kopalni, zapłacić koszt i spełnić zasięg. Gaiaformer oznacza rezerwację dla właściciela, ale dokładne interakcje frakcji i dodatku wymagają wpisów w FAQ.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Celina w rundzie 3 uruchamia Projekt Gaja na Transdim w zasięgu: płaci koszt z toru, odkłada moc do obszaru Gaja, stawia Gaiaformer. W Fazie Gai rundy 4 planeta staje się Gają. Dopiero w fazie akcji rundy 4 (lub później) buduje na niej kopalnię — nie dostaje jej automatycznie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: myśleć, że po Projekcie Gaja kopalnia powstaje automatycznie, albo stawiać zwykłą kopalnię na Transdim przed transformacją.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: Faza Gai w strukturze rundy — rozdział 4; budowa na Gai — rozdział 8.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Projekt Gaja rezerwuje Transdim; kopalnia to osobna, późniejsza akcja. P: Czy kopalnia powstaje sama po Fazie Gai? O: Nie. P: Czy ktoś inny może zbudować na Twoim Gaiaformerze? O: Gaiaformer rezerwuje dla właściciela — wyjątki frakcji/dodatku sprawdzaj w FAQ komponentów.',
      },
    ],
  },
  {
    title: '14. Tory badań',
    content: [
      {
        type: 'heading',
        text: 'Co dają badania',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Istnieje sześć dziedzin badań. Akcja badań albo zdobycie technologii pozwala przesunąć znacznik o jedno pole. Tory dają bonusy po drodze i punkty na końcu gry.',
      },
      {
        type: 'step-list',
        steps: [
          'nie można wejść na najwyższy poziom bez zielonego tokena federacji;',
          'na szczycie danego toru zwykle może znajdować się ograniczona liczba graczy;',
          'niektóre pola dają natychmiastowy bonus;',
          'tor badań zapewnia punkty na końcu gry.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Niektóre technologie zaawansowane wymagają przykrycia posiadanej technologii standardowej i odpowiedniego poziomu na powiązanym torze.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna ma zielony token federacji i chce wejść na szczyt toru. Bez tokena ruch na najwyższy poziom jest nielegalny — najpierw domyka federację, potem awansuje.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wchodzić na szczyt toru bez zielonego tokena federacji albo ignorować limit graczy na szczycie.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: technologie — rozdział 15; federacje — rozdział 16; punktowanie badań — rozdział 21.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sześć torów: awans z akcji lub technologii, szczyt wymaga zielonego tokena. P: Czy każdy awans daje technologię? O: Nie — technologia ma własne reguły. P: Czy szczyt jest nielimitowany? O: Zwykle liczba graczy na szczycie jest ograniczona.',
      },
    ],
  },
  {
    title: '15. Technologie',
    content: [
      {
        type: 'heading',
        text: 'Standardowe i zaawansowane',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Technologie zmieniają dochód, dają akcje specjalne, efekty natychmiastowe albo punktowanie. Tekst kafla ma pierwszeństwo przed ogólnym opisem — pełny katalog jest zablokowany do weryfikacji.',
      },
      {
        type: 'heading',
        text: 'Standardowe',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dają efekt natychmiastowy, dochód, akcję specjalną albo punktowanie.',
      },
      {
        type: 'heading',
        text: 'Zaawansowane',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Są silniejsze i wymagają: odpowiedniej pozycji na torze, przykrycia technologii standardowej oraz spełnienia dodatkowych warunków.',
      },
      {
        type: 'paragraph',
        text: 'W aplikacji każda technologia musi mieć osobny wpis z timingiem, aliasami ikon i przykładami. [DO WERYFIKACJI: katalog kafli].',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bartek ulepsza do laboratorium i należy mu się technologia. Bierze standardową z dostępnej półki. Później, mając poziom toru i wolną standardową do przykrycia, sięga po zaawansowaną — bez przykrycia ruch jest nielegalny, chyba że wyraźna zdolność mówi inaczej.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: brać zaawansowaną technologię bez przykrywania standardowej albo traktować katalog aplikacji jako kompletny, gdy jest oznaczony do weryfikacji.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: ulepszenia dające technologię — rozdział 11; tory — rozdział 14.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Standardowe są dostępniejsze; zaawansowane wymagają toru i przykrycia. P: Czy mogę wziąć zaawansowaną bez przykrycia? O: Nie, chyba że wyraźna zdolność mówi inaczej. P: Skąd brać pełny tekst kafla? O: Z komponentu — katalog jest DO WERYFIKACJI.',
      },
    ],
  },
  {
    title: '16. Federacje',
    content: [
      {
        type: 'heading',
        text: 'Czym jest federacja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Federacja to połączona sieć własnych struktur o wymaganej łącznej wartości mocy. Token federacji jest nagrodą za legalną, minimalną sieć — nie za „jak najwięcej satelitów”.',
      },
      {
        type: 'heading',
        text: 'Procedura utworzenia',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wskaż grupę własnych budynków;',
          'połącz je najmniejszą legalną liczbą satelitów;',
          'sprawdź wymaganą sumę wartości;',
          'wydaj po jednym znaczniku mocy za każdy satelita;',
          'wybierz dostępny token federacji;',
          'oznacz wszystkie użyte struktury jako należące do tej federacji.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Zwykle struktura nie może należeć do drugiej federacji, chyba że nowa federacja łączy istniejące federacje zgodnie z wyjątkami. Sieć musi być minimalna: nie dodawaj satelitów tylko po to, by sztucznie zwiększyć obszar.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna łączy trzy budynki o wystarczającej sumie wartości. Potrzebuje 2 satelitów — płaci 2 mocy z misy III, bierze token, oznacza struktury. Nie dolicza satelitów do wartości federacji i nie dokłada trzeciego satelity „na zapas”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wliczanie satelitów do wartości federacji, używanie jednego budynku w wielu niezależnych federacjach albo dokładanie zbędnych satelitów.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: zielony token i szczyt toru badań — rozdział 14. Wartości struktur sprawdzaj na komponentach.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Federacja = własne struktury + minimalne satelity + wymagana moc + token. P: Czy satelity liczą się do wartości? O: Nie. P: Czy ten sam budynek może być w dwóch niezależnych federacjach? O: Zwykle nie — wyjątki przy łączeniu federacji.',
      },
    ],
  },
  {
    title: '17. Akcje mocy i Q.I.C.',
    content: [
      {
        type: 'heading',
        text: 'Wspólne pola akcji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pola akcji mocy i Q.I.C. są dostępne wspólnie. Po wykonaniu akcji zakryj ją znacznikiem, aby inni nie mogli jej użyć w tej rundzie.',
      },
      {
        type: 'heading',
        text: 'Zaginiona Flota a stare lokalizacje',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W Zaginionej Flocie część podstawowych akcji Q.I.C. przenoszą statki i plansze eksploracji. Nie korzystaj jednocześnie ze starej i nowej lokalizacji tej samej akcji.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bartek wykonuje wspólną akcję mocy, kładzie znacznik na polu — Celina w tej rundzie musi wybrać inne dostępne pole. W partii z Zaginioną Flotą nie używa jednocześnie starego pola Q.I.C. i jego zastępstwa ze statku.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: łączenie podstawowych akcji Q.I.C. z ich zastępstwami z Zaginionej Floty albo zostawianie pola odkrytego po użyciu.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: pełna procedura eksploracji — rozdział 20 (Zaginiona Flota).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wspólne akcje: użyj i zakryj na rundę. P: Czy ktoś inny może użyć tego samego pola w tej rundzie? O: Nie po zakryciu. P: Czy Flota i baza mogą dublować tę samą akcję Q.I.C.? O: Nie — nie łącz starej i nowej lokalizacji.',
      },
    ],
  },
  {
    title: '18. Pasowanie',
    content: [
      {
        type: 'heading',
        text: 'Co robi pas',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pasowanie kończy Twój udział w fazie akcji tej rundy i ustala miejsce w kolejności następnej rundy. To nie jest „pauza, z której wrócisz”.',
      },
      {
        type: 'step-list',
        steps: [
          'nie wykonujesz już akcji w tej rundzie;',
          'zdobądź punkty z użytego boostera, jeśli zawiera efekt pasowania;',
          'wybierz nowy dostępny booster;',
          'odłóż stary booster;',
          'zajmij pierwsze wolne miejsce kolejności następnej rundy.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Nie można spasować i wrócić do gry w tej samej rundzie.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna pasuje jako pierwsza: bierze punkty z boostera (jeśli ma efekt pasowania), wybiera nowy booster, odkłada stary i zajmuje pierwsze miejsce kolejności. Bartek może jeszcze wykonywać akcje; Anna już nie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wykonywanie akcji po spasowaniu albo zostawianie starego boostera „na wszelki wypadek”.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: kolejność nowej rundy w strukturze — rozdział 4. Boostery: teksty kafli DO WERYFIKACJI w katalogu.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pas = koniec akcji w rundzie + nowy booster + miejsce w kolejności. P: Czy booster zostaje po pasowaniu? O: Wybierasz nowy booster dla następnej rundy. P: Czy mogę wrócić po pasie? O: Nie w tej rundzie.',
      },
    ],
  },
  {
    title: '19. Cele rund i cele końcowe',
    content: [
      {
        type: 'heading',
        text: 'Kiedy punktuje cel rundy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel rundy punktuje konkretne czynności wykonane w bieżącej rundzie. Sprawdź go w chwili wykonania akcji, nie na końcu rundy, chyba że kafel mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Cele końcowe',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cele końcowe porównują graczy po 6. rundzie. Gracze zajmują miejsca i otrzymują punkty według tabeli. Wirtualny gracz neutralny może uczestniczyć w rankingu w rozgrywkach o mniejszej liczbie graczy.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel rundy nagradza ulepszenie. Bartek ulepsza laboratorium i od razu kładzie znacznik / bierze punkty. Nie czeka do porządkowania „na zbiorowe rozliczenie”, bo kafel nie każe czekać.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: rozliczać cel rundy dopiero na końcu rundy, gdy kafel wymaga sprawdzenia w chwili akcji.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: teksty konkretnych celów — [DO WERYFIKACJI: katalog celów]. Koniec gry — rozdział 21.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel rundy = zwykle w chwili akcji; cele końcowe = po 6. rundzie. P: Czy zawsze czekam do końca rundy z celem? O: Nie, chyba że kafel mówi inaczej. P: Co z mniejszą liczbą graczy? O: Może wejść wirtualny gracz neutralny w rankingu.',
      },
    ],
  },
  {
    title: 'Zaginiona Flota',
    requiredAddons: ['lost-fleet'],
    content: [
      {
        type: 'warning',
        text: 'Jeden rozdział dodatku (ONE EXPANSION = ONE CHAPTER). Statki, plansze eksploracji, artefakty, nowe technologie/boostery/tokeny oraz pełne plansze czterech frakcji wymagają osobnej weryfikacji katalogu — nie zgaduj kosztów ani tekstów. [DO WERYFIKACJI: pełne plansze frakcji, statki, artefakty, technologie Floty].',
      },
      {
        type: 'heading',
        text: 'Co zmienia dodatek',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zaginiona Flota zmienia mapę i dodaje eksplorację głębokiej przestrzeni. Część podstawowych akcji Q.I.C. przenoszą statki i plansze eksploracji — nie korzystaj jednocześnie ze starej i nowej lokalizacji tej samej akcji.',
      },
      {
        type: 'heading',
        text: 'Nowe obszary',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'pola międzysektorowe i głębokiej przestrzeni;',
          'asteroidy i protoplanety;',
          'miejsca docelowe dla statków;',
          'plansze eksploracji.',
        ],
      },
      {
        type: 'heading',
        text: 'Przygotowanie z dodatkiem',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zastosuj nowy sposób przygotowania mapy, plansze statków i eksploracji oraz elementy zastępujące podstawowe akcje Q.I.C. Dokładna liczba elementów zależy od liczby graczy i wybranego układu — odczytaj z oficjalnego setupu Floty.',
      },
      {
        type: 'heading',
        text: 'Statki i akcja Eksploracji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Aby wykonać akcję Eksploracji:',
      },
      {
        type: 'step-list',
        steps: [
          'wybierz legalny statek lub planszę;',
          'sprawdź koszt podany na planszy eksploracji — często obejmuje 5 PZ, ale zdolności i frakcje mogą go zmieniać;',
          'wybierz dostępny prom/znacznik eksploracji;',
          'sprawdź zasięg z własnych struktur;',
          'umieść znacznik i wykonaj nagrodę lub odblokuj akcję;',
          'zastosuj zasady blokowania i kolejności.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Nie wpisuj uniwersalnego kosztu bez sprawdzenia konkretnej planszy.',
      },
      {
        type: 'heading',
        text: 'Nowe frakcje',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Darkanianie;',
          'Moweydowie;',
          'Kosmiczni Giganci;',
          'Tinkeroidzi.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Każda wymaga osobnego setupu i katalogu zdolności. [DO WERYFIKACJI: pełne plansze frakcji].',
      },
      {
        type: 'heading',
        text: 'Artefakty, technologie i federacje',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dodatek wprowadza nowe artefakty, technologie, boostery i tokeny federacji. Ich tekst ma pierwszeństwo przed zasadą ogólną. Każdy element musi być filtrowany przez moduł lost-fleet.',
      },
      {
        type: 'heading',
        text: 'Przykład eksploracji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna wybiera legalny statek, czyta koszt z konkretnej planszy eksploracji (nie z pamięci „zawsze 5 PZ”), sprawdza zasięg od własnych struktur, kładzie znacznik i wykonuje nagrodę. Nie używa jednocześnie starego pola Q.I.C. zastąpionego przez ten statek.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: uniwersalny koszt eksploracji bez odczytu planszy; dublowanie akcji Q.I.C. baza+Flota; granie Floty w First Game przed opanowaniem podstawy.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: akcje mocy/Q.I.C. w podstawie — rozdział 17; First Game zaleca partię bez Floty. Filtruj treści modułem lost-fleet.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Eksploracja w Zaginionej Flocie',
        fallbackText: 'Schemat: statek/plansza → koszt z planszy → prom → zasięg → znacznik i nagroda; bez dublowania starej akcji Q.I.C.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Flota = nowa mapa + eksploracja + frakcje/artefakty; jeden rozdział menu. P: Czy koszt eksploracji jest zawsze 5 PZ? O: Często, ale sprawdź planszę i zdolności. P: Czy mogę używać starego i nowego pola tej samej akcji Q.I.C.? O: Nie.',
      },
    ],
  },
  {
    title: '21. Koniec gry i punktowanie',
    content: [
      {
        type: 'heading',
        text: 'Kiedy kończysz liczyć',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po 6. rundzie nie zgadujesz zwycięzcy z mapy — przechodzisz formalne punktowanie końcowe.',
      },
      {
        type: 'step-list',
        steps: [
          'rozpatrz dwa cele końcowe;',
          'przyznaj punkty za pozycje na torach badań;',
          'przelicz pozostałe zasoby według oficjalnego kursu;',
          'dodaj punkty technologii, frakcji i dodatku;',
          'rozstrzygnij remis.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Aplikacja powinna zawierać kalkulator i przykład końcowego liczenia. [DO WERYFIKACJI: oficjalny kurs zasobów, pełne punkty technologii/frakcji oraz reguła remisu wydania].',
      },
      {
        type: 'heading',
        text: 'Przykład końcowego liczenia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po rundzie 6 Anna i Bartek porównują dwa cele końcowe (miejsca w rankingu), dodają punkty z torów badań, przeliczają pozostałe zasoby po oficjalnym kursie, dopisują punkty z technologii/frakcji (i Floty, jeśli grają z dodatkiem), na końcu stosują oficjalną regułę remisu — bez wymyślania „kto miał więcej planet”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijać cele końcowe albo przeliczać zasoby „na oko” zamiast oficjalnym kursem.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: cele — rozdział 19; badania — rozdział 14. Remis: DO WERYFIKACJI względem wydania.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Punktowanie końcowe Projektu Gaja',
        fallbackText: 'Schemat: cele końcowe → tory badań → kurs zasobów → technologie/frakcje/dodatek → remis.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koniec = cele + badania + zasoby + efekty (+ remis). P: Czy mapa sama rozstrzyga wygraną? O: Nie — liczysz punkty. P: Skąd kurs zasobów? O: Z oficjalnej tabeli wydania.',
      },
    ],
  },
  {
    title: 'QUICK REFERENCE',
    content: [
      {
        type: 'paragraph',
        text: 'Dochód → Gaja → akcje naprzemienne → pasowanie → porządkowanie.',
      },
      {
        type: 'paragraph',
        text: 'Akcje: kopalnia, Gaia, ulepszenie, federacja, badania, moc/Q.I.C., specjalna, pas.',
      },
      {
        type: 'paragraph',
        text: 'Budowa: zasięg + terraforming + koszt kopalni. Federacja: wymagana moc + minimalne satelity + token. Koniec: cele końcowe + badania + zasoby + efekty.',
      },
      {
        type: 'tip',
        text: 'Flota: eksploracja z kosztem z planszy; bez dublowania starych akcji Q.I.C.',
      },
    ],
  },
  {
    title: 'NAJCZĘSTSZE BŁĘDY',
    content: [
      {
        type: 'paragraph',
        text: 'Poniższe pomyłki wracają przy prawie każdym stole początkującym. Każdy punkt odpowiada procedurze opisanej w rozdziałach powyżej — jeśli któryś brzmi znajomo, wróć do wskazanego tematu zanim „dogrywacie na domysł”.',
      },
      {
        type: 'step-list',
        steps: [
          'Traktowanie ładowania mocy jak dobierania nowych znaczników — dochód mocy przesuwa istniejące znaczniki przez misy.',
          'Pomijanie kosztu terraformingu przy budowie kopalni.',
          'Zostawianie częściowo terraformowanej planety „na później” — terraforming i budowa to jedna akcja.',
          'Wliczanie satelitów do wartości federacji — satelity łączą, nie dodają mocy struktur.',
          'Używanie jednego budynku w wielu niezależnych federacjach (poza wyjątkami łączenia federacji).',
          'Dobieranie technologii przy każdym ulepszeniu, także przy stacji handlowej.',
          'Wykonywanie akcji po spasowaniu.',
          'Łączenie podstawowych akcji Q.I.C. z ich zastępstwami z Zaginionej Floty.',
        ],
      },
    ],
  },
  {
    title: 'FAQ',
    content: [
      {
        type: 'heading',
        text: 'Budowa i mapa',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę zbudować kopalnię na planecie zajętej przez przeciwnika? Nie.',
      },
      {
        type: 'paragraph',
        text: 'Czy planeta w zasięgu musi być połączona prostą linią? Nie. Liczy się najmniejsza liczba połączeń między polami.',
      },
      {
        type: 'heading',
        text: 'Moc',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę odmówić pasywnego ładowania mocy? Tak.',
      },
      {
        type: 'paragraph',
        text: 'Czy palenie mocy jest akcją główną? Nie, jest czynnością darmową wykonywaną w dozwolonym momencie.',
      },
      {
        type: 'heading',
        text: 'Projekt Gaja i technologie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy po Projekcie Gaja kopalnia powstaje automatycznie? Nie. Po ukończeniu transformacji potrzebna jest osobna akcja budowy.',
      },
      {
        type: 'paragraph',
        text: 'Czy mogę wziąć zaawansowaną technologię bez przykrywania standardowej? Nie, chyba że wyraźna zdolność mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Pasowanie i boostery',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy booster pozostaje po pasowaniu? Wybierasz nowy booster dla następnej rundy.',
      },
      {
        type: 'paragraph',
        text: 'Czy akcja specjalna z boostera lub technologii zużywa turę jak inne akcje główne? Tak — to pozycja z listy akcji głównych, nie free action.',
      },
    ],
  },
  {
    title: 'GLOSSARY',
    content: [
      {
        type: 'step-list',
        steps: [
          'Gaiaformer — element inicjujący Projekt Gaja.',
          'Q.I.C. — rzadki zasób technologiczny.',
          'Satelita — znacznik łączący struktury federacji.',
          'Federacja — połączona sieć własnych struktur.',
          'Transdim — planeta wymagająca Projektu Gaja.',
          'Palenie mocy — usunięcie jednego znacznika z misy II w celu przesunięcia innego do misy III.',
          'Booster — kafel rundy zapewniający dochód, akcję lub punkty przy pasowaniu.',
        ],
      },
    ],
  },
];

export const projektGajaFirstGame: FirstGameStep[] = [
  {
    id: 1,
    title: 'Bez Zaginionej Floty',
    content: [
      'Zagraj pierwszą partię bez Zaginionej Floty — nauczysz się zegara rundy, mocy i budowy bez nowej mapy eksploracji.',
    ],
    why: 'Lokalny First Game zaleca podstawę przed dodatkiem.',
    commonMistake: 'Dokładanie Floty od razu i mieszanie starych akcji Q.I.C. z zastępstwami.',
    chapterIndex: 3,
  },
  {
    id: 2,
    title: 'Proste frakcje',
    content: [
      'Wybierz frakcje o prostych zdolnościach i przygotuj plansze według ich setupu.',
    ],
    why: 'Mniej wyjątków na start — łatwiej złapać wspólne procedury.',
    commonMistake: 'Wybór frakcji o wyjątkach, których katalog nie jest jeszcze zweryfikowany w aplikacji.',
    chapterIndex: 2,
  },
  {
    id: 3,
    title: 'Rekomendowana mapa',
    content: [
      'Ustaw rekomendowaną mapę dla liczby graczy (wskazany układ sektorów podstawy).',
    ],
    why: 'Zły układ psuje zasięg i sąsiedztwo od pierwszej rundy.',
    commonMistake: 'Układ z innej liczby graczy albo setup Floty w First Game.',
    chapterIndex: 2,
  },
  {
    id: 4,
    title: 'Pierwsza runda: kopalnia i ulepszenie',
    content: [
      'W 1. rundzie wykonaj co najmniej jedną kopalnię i jedno ulepszenie, licząc zasięg i terraforming.',
    ],
    why: 'Ćwiczysz rdzeń akcji gospodarczych, zanim wejdziesz w federacje.',
    commonMistake: 'Pomijanie kosztu terraformingu albo budowa na Transdim bez Projektu Gaja.',
    chapterIndex: 7,
  },
  {
    id: 5,
    title: 'Cykl mocy na stole',
    content: [
      'Pokaż cykl mocy na fizycznych znacznikach: ładowanie I→II→III, wydawanie z III, ewentualne palenie w II.',
    ],
    why: 'Dochód mocy to przesuwanie znaczników, nie dobieranie nowych.',
    commonMistake: 'Dobieranie nowych znaczników mocy z pudełka przy dochodzie.',
    chapterIndex: 5,
  },
  {
    id: 6,
    title: 'Pas ustala kolejność',
    content: [
      'Wyjaśnij, że pasowanie kończy akcje w rundzie i ustala kolejność następnej rundy oraz wymianę boostera.',
    ],
    why: 'Kolejność nowej rundy wynika z pasów, nie z gestu „kto zaczynał”.',
    commonMistake: 'Wykonywanie akcji po spasowaniu.',
    chapterIndex: 17,
  },
  {
    id: 7,
    title: 'Federacje przed 3. rundą',
    content: [
      'Federacje i zaawansowane technologie omów przed 3. rundą: minimalne satelity, token, przykrycie standardowej technologii.',
    ],
    why: 'To częste źródło błędów, gdy gracze „domykają sieć na zapas”.',
    commonMistake: 'Wliczanie satelitów do wartości federacji; zaawansowana technologia bez przykrycia.',
    chapterIndex: 15,
  },
];
